import React, {
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import classnames from 'classnames';
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Slide,
  Slider
} from 'pure-react-carousel';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import WiseProductItemSkeleton from 'pages/wise/WiseProduct/components/WiseProductItem/WiseProductItemSkeleton';

import styles from 'theme/pages/wise/WiseProduct/components/WiseCarousel/WiseCarousel.module.scss';

const WiseCarouselEdgeFades: FC<{
  totalSlides: number;
  visibleSlides: number;
}> = ({ totalSlides: totalSlidesProp, visibleSlides: visibleSlidesProp }) => {
  const carouselStore = useContext(CarouselContext);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useLayoutEffect(() => {
    if (!carouselStore) {
      return undefined;
    }

    const sync = () => {
      const s = carouselStore.getStoreState();
      const total = s.totalSlides;
      const visible = Math.min(Math.max(1, s.visibleSlides), total);
      const maxStart = Math.max(0, total - visible);
      setShowLeft(s.currentSlide > 0);
      setShowRight(s.currentSlide < maxStart);
    };

    sync();
    carouselStore.subscribe(sync);
    return () => {
      carouselStore.unsubscribe(sync);
    };
  }, [carouselStore, totalSlidesProp, visibleSlidesProp]);

  return (
    <>
      <div
        className={classnames(
          styles.edgeFade,
          styles.edgeFadeLeft,
          showLeft && styles.edgeFadeVisible
        )}
        aria-hidden
      />
      <div
        className={classnames(
          styles.edgeFade,
          styles.edgeFadeRight,
          showRight && styles.edgeFadeVisible
        )}
        aria-hidden
      />
    </>
  );
};

const WiseCarouselSlideSync: FC<{
  targetSlide: number;
  totalSlides: number;
  visibleSlides: number;
  revision: number;
}> = ({ targetSlide, totalSlides, visibleSlides, revision }) => {
  const carouselStore = useContext(CarouselContext);

  useLayoutEffect(() => {
    if (!carouselStore || totalSlides < 1) {
      return undefined;
    }
    const vis = Math.min(Math.max(1, visibleSlides), totalSlides);
    const maxStart = Math.max(0, totalSlides - vis);
    const clamped = Math.max(0, Math.min(targetSlide, maxStart));
    const s = carouselStore.getStoreState();
    if (s.currentSlide !== clamped) {
      carouselStore.setStoreState({ currentSlide: clamped });
    }
    return undefined;
  }, [carouselStore, targetSlide, totalSlides, visibleSlides, revision]);

  return null;
};

export type WiseCarouselTab = {
  id: string;
  label: ReactNode;
};

export type WiseCarouselProps = {
  totalSlides: number;
  visibleSlides: number;
  naturalSlideWidth?: number;
  naturalSlideHeight?: number;
  isIntrinsicHeight?: boolean;
  disableAnimation?: boolean;
  disableKeyboard?: boolean;
  title?: ReactNode;
  tabs?: WiseCarouselTab[];
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  showNavArrows?: boolean;
  headerAction?: ReactNode;
  /**
   * Układ nagłówka:
   *  - `inline` (default) - tytuł i CTA w jednym wierszu (desktop + mobile).
   *  - `stacked-mobile` - od `breakpoint(md)` CTA leci pod tytuł (np. mobilny WiseProductsBox,
   *    gdzie długi tytuł kategorii ucinał elipsą i CTA było niewidoczne).
   */
  headerLayout?: 'inline' | 'stacked-mobile';
  /** Gdy `true` i `totalSlides < 1`, zamiast `children` renderowane są szkielety produktów (np. pierwsze ładowanie). */
  isLoading?: boolean;
  /** Liczba slajdów-szkieletów; gdy brak - używane jest `visibleSlides` (wtedy np. mobile z `visibleSlides={1}` da tylko 1 szkielet). */
  skeletonTotalSlides?: number;
  /** Gdy są już slajdy, ale trwa ponowne pobieranie - warstwa ze szkieletami (np. zmiana zakładki). */
  loadingOverlay?: boolean;
  /** Liczba szkieletów w `loadingOverlay`; domyślnie `visibleSlides`. */
  loadingOverlaySlides?: number;
  /** Przywrócenie indeksu slajdu (np. po szkielecie / zmianie zakładki); wymaga `slideSyncRevision`. */
  syncSlideIndex?: number;
  /** Zwiększ po zmianie zakładki lub zejściu ze szkieletu - wymusza `setStoreState` slajdu. */
  slideSyncRevision?: number;
  children?: ReactNode;
  className?: string;
  relaxHeight?: boolean;
};

const WiseCarousel: FC<WiseCarouselProps> = ({
  totalSlides,
  visibleSlides,
  naturalSlideWidth = 0,
  naturalSlideHeight = 0,
  isIntrinsicHeight: isIntrinsicHeightProp,
  disableAnimation,
  disableKeyboard,
  title,
  tabs,
  activeTabId,
  onTabChange,
  showNavArrows = true,
  headerAction,
  headerLayout = 'inline',
  isLoading,
  skeletonTotalSlides: skeletonTotalSlidesProp,
  loadingOverlay,
  loadingOverlaySlides: loadingOverlaySlidesProp,
  syncSlideIndex,
  slideSyncRevision,
  children,
  className,
  relaxHeight
}) => {
  const skeletonMode = Boolean(isLoading) && totalSlides < 1;
  const wasSkeletonModeRef = useRef(false);
  const [freezeTrayAfterSkeleton, setFreezeTrayAfterSkeleton] = useState(false);

  useLayoutEffect(() => {
    if (skeletonMode) {
      wasSkeletonModeRef.current = true;
      return undefined;
    }
    if (!wasSkeletonModeRef.current) {
      return undefined;
    }
    wasSkeletonModeRef.current = false;
    setFreezeTrayAfterSkeleton(true);
    return undefined;
  }, [skeletonMode]);

  useEffect(() => {
    if (!freezeTrayAfterSkeleton) return undefined;
    const id = window.setTimeout(() => setFreezeTrayAfterSkeleton(false), 420);
    return () => window.clearTimeout(id);
  }, [freezeTrayAfterSkeleton]);

  if (!skeletonMode && totalSlides < 1) {
    return null;
  }

  const skeletonCount = Math.max(
    1,
    skeletonTotalSlidesProp ?? visibleSlides
  );
  const effectiveTotalSlides = skeletonMode ? skeletonCount : totalSlides;
  const effectiveVisibleSlides = skeletonMode
    ? Math.min(Math.max(1, visibleSlides), skeletonCount)
    : visibleSlides;

  const isIntrinsicHeight = isIntrinsicHeightProp ?? true;

  const hasTabs = Boolean(tabs?.length);
  const hasTitle = Boolean(title);
  const hasLeft = hasTabs || hasTitle;
  const navArrowsVisible = showNavArrows && !skeletonMode;
  const hasRight = navArrowsVisible || Boolean(headerAction);
  const showHeader = hasLeft || hasRight;

  const overlaySkeletonCount = Math.max(
    1,
    loadingOverlaySlidesProp ?? effectiveVisibleSlides
  );
  const showLoadingOverlay = Boolean(loadingOverlay) && !skeletonMode && totalSlides >= 1;

  const enableSlideSync =
    !skeletonMode &&
    typeof syncSlideIndex === 'number' &&
    typeof slideSyncRevision === 'number';

  const sliderBody = skeletonMode ? (
    <Slider>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <Slide key={index} index={index} className={styles.skeletonSlide}>
          <div className={styles.skeletonItemWrap}>
            <WiseProductItemSkeleton />
          </div>
        </Slide>
      ))}
    </Slider>
  ) : (
    children
  );

  return (
    <CarouselProvider
      key={skeletonMode ? 'wise-carousel-skeleton' : 'wise-carousel-main'}
      naturalSlideWidth={naturalSlideWidth}
      naturalSlideHeight={naturalSlideHeight}
      totalSlides={effectiveTotalSlides}
      visibleSlides={effectiveVisibleSlides}
      currentSlide={skeletonMode ? 0 : undefined}
      isIntrinsicHeight={isIntrinsicHeight}
      disableAnimation={
        disableAnimation || skeletonMode || freezeTrayAfterSkeleton
      }
      disableKeyboard={disableKeyboard}
      className={classnames(
        styles.provider,
        className,
        'StylePath-Pages-Wise-WiseProduct-components-WiseCarousel'
      )}>
      <div className={classnames(styles.root, relaxHeight && styles.rootRelaxed)}>
        {showHeader && (
          <div
            className={classnames(
              styles.header,
              hasTabs && styles.headerWithTabs,
              !hasLeft && styles.headerOnlyActions,
              headerLayout === 'stacked-mobile' && styles.headerStackedMobile
            )}
            role={hasTabs ? 'tablist' : undefined}>
            <div
              className={classnames(
                styles.headerMain,
                hasTabs && styles.headerMainWithTabs
              )}>
              {hasTabs ? (
                <div className={styles.tabs}>
                  {tabs!.map((tab) => {
                    const isActive = tab.id === activeTabId;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={classnames(styles.tab, isActive && styles.tabActive)}
                        onClick={() => onTabChange?.(tab.id)}>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              ) : hasTitle ? (
                <div className={styles.title}>{title}</div>
              ) : null}
            </div>
            {hasRight && (
              <div className={styles.headerActions}>
                {navArrowsVisible && (
                  <>
                    <ButtonBack className={classnames(styles.navButton, 'wise-carousel-nav-back')}>
                      <ChevronLeft aria-hidden />
                    </ButtonBack>
                    <ButtonNext className={classnames(styles.navButton, 'wise-carousel-nav-next')}>
                      <ChevronRight aria-hidden />
                    </ButtonNext>
                  </>
                )}
                {headerAction ? <div className={styles.actionSlot}>{headerAction}</div> : null}
              </div>
            )}
          </div>
        )}
        <div className={styles.slider}>
          {sliderBody}
          {enableSlideSync ? (
            <WiseCarouselSlideSync
              targetSlide={syncSlideIndex}
              revision={slideSyncRevision}
              totalSlides={effectiveTotalSlides}
              visibleSlides={effectiveVisibleSlides}
            />
          ) : null}
          <WiseCarouselEdgeFades
            totalSlides={effectiveTotalSlides}
            visibleSlides={effectiveVisibleSlides}
          />
          {showLoadingOverlay && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingOverlayStrip}>
                {Array.from({ length: overlaySkeletonCount }).map((_, index) => (
                  <WiseProductItemSkeleton key={index} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </CarouselProvider>
  );
};

export default WiseCarousel;
